import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Modal } from '../Modal';
import { SecondaryButton, PrimaryButton, Chip, TabBar, TabItem, UnstyledButton } from '../Button';
import DateRangePickerModal from '../DateRangePickerModal';
import mediaService from '../../../services/mediaService';
import mediaLibraryService from '../../../services/mediaLibraryService';
import MediaGalleryGrid from '../MediaGalleryGrid';
import MediaModal from '../MediaPlayer/MediaModal';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import VipIcon from '../../Icons/VipIcons';
import { VIEW_ALL, VIEW_FAVORITES, VIEW_FOLDER } from '../MediaLibraryFolderTree';

// ==================== STYLED COMPONENTS ====================

const PickerContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PickerBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
`;

const MainWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;

  @media (max-width: 599px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  flex: 1;

  &:first-child {
    flex: 0 0 auto;
  }
`;

const FilterButton = styled(Chip)`
  border-radius: 20px;
  border: 1px solid ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $active }) => $active ? theme.colors.primaryBg || `${theme.colors.primary}18` : 'transparent'};
  color: ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.textMuted};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${({ theme, $active }) => $active ? theme.colors.primaryBg || `${theme.colors.primary}18` : 'transparent'};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const SearchInput = styled.input`
  flex: 1 1 0;
  min-width: 0;
  max-width: 260px;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bg2};
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.8125rem;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SortButton = styled(Chip).attrs({ $size: 'small' })`
  flex-shrink: 0;
  line-height: 1;
`;

const DateRangeChip = styled(Chip).attrs({ $size: 'small', $active: true })`
  padding-right: 0.625rem;
  flex-shrink: 0;
`;

const ChipClear = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  line-height: 0;

  &:hover {
    opacity: 1;
  }
`;


const GridContainer = styled.div`
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 1rem 0;
  max-height: 55vh;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 3rem 2rem;
  text-align: center;
  gap: 0.5rem;

  p {
    font-size: 0.9375rem;
    margin: 0;
  }

  small {
    font-size: 0.8125rem;
    opacity: 0.7;
  }
`;

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
`;

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 0 0;
`;

const FooterRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 100%;

  > button {
    flex: 1;
    width: auto;
  }
`;

const LoadMoreButton = styled(SecondaryButton).attrs({ $size: 'small' })``;

const SelectionOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.primary}40;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: inherit;
  pointer-events: none;
`;

const SelectionCheckbox = styled(UnstyledButton)`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 2;
  color: ${({ $selected, theme }) => $selected ? theme.colors.primary : 'rgba(0,0,0,0.35)'};
  box-shadow: 0 1px 4px rgba(0,0,0,0.4);
  transition: color 0.12s ease, transform 0.12s ease;

  &:hover {
    background: #fff;
    color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.1);
  }
`;

const TabRow = styled(TabBar)`
  padding: 12px 4px;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.bg};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    gap: 4px;
    padding: 8px 4px;
  }
`;



// ==================== CONSTANTS ====================

const TYPE_FILTERS = [
  { label: 'All', value: null },
  { label: 'Images', value: 'IMAGE' },
  { label: 'Videos', value: 'VIDEO' },
  { label: 'Audio', value: 'AUDIO' },
];

const PAGE_SIZE = 24;

// ==================== COMPONENT ====================

/**
 * MediaLibraryPicker
 *
 * A modal that lets the user browse and select previously uploaded media
 * from their Upload Library to reuse in a post or message.
 *
 * Props:
 *   onSelect(items)  — called with array of selected library items on confirm
 *   onClose()        — called when dismissed without selecting
 *   maxSelections    — maximum number of items that can be selected (default 10)
 */
const MediaLibraryPicker = ({ onSelect, onClose, maxSelections = 10 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [typeFilter, setTypeFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [previewIndex, setPreviewIndex] = useState(null);
  const [sortDir, setSortDir] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selection, setSelection] = useState({ view: VIEW_ALL, folderId: null });
  const mq = useRef(typeof window !== 'undefined' ? window.matchMedia('(max-width: 599px)') : null);
  const [isMobile, setIsMobile] = useState(() => mq.current?.matches ?? false);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    const media = mq.current;
    if (!media) return;
    const handler = (e) => setIsMobile(e.matches);
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  // Debounce search input
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 350);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]);

  // Load folder tree once
  useEffect(() => {
    let cancelled = false;
    mediaLibraryService.listFolders('tree').then((data) => {
      if (!cancelled) setFolders(Array.isArray(data) ? data : []);
    }).catch((err) => {
      console.warn('[MediaLibraryPicker] Failed to load folders:', err);
    });
    return () => { cancelled = true; };
  }, []);

  const flatFolders = useMemo(() => {
    const out = [];
    const walk = (nodes) => {
      for (const n of nodes) {
        out.push(n);
        if (Array.isArray(n.children) && n.children.length) walk(n.children);
      }
    };
    walk(folders);
    return out;
  }, [folders]);

  const tabItems = useMemo(() => ([
    { id: 'all', label: 'All' },
    { id: 'favorites', label: '★ Favorites' },
    ...flatFolders.map((f) => ({ id: `folder:${f.id}`, label: f.name })),
  ]), [flatFolders]);

  const activeTabId =
    selection.view === VIEW_FAVORITES ? 'favorites'
    : selection.view === VIEW_FOLDER && selection.folderId ? `folder:${selection.folderId}`
    : 'all';

  const handleTabClick = (id) => {
    if (id === 'all') setSelection({ view: VIEW_ALL, folderId: null });
    else if (id === 'favorites') setSelection({ view: VIEW_FAVORITES, folderId: null });
    else if (id.startsWith('folder:')) setSelection({ view: VIEW_FOLDER, folderId: id.slice(7) });
  };

  // Reset and re-fetch only when actual filter/sort values change.
  useEffect(() => {
    setItems([]);
    setPage(0);
    setHasMore(true);
    fetchPage(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, debouncedSearch, sortDir, dateFrom, dateTo, selection]);

  const fetchPage = useCallback(async (pageNum, reset = false) => {
    try {
      if (pageNum === 0) setLoading(true);
      else setLoadingMore(true);

      let result;
      if (selection.view === VIEW_FAVORITES) {
        result = await mediaLibraryService.listFavorites({
          page: pageNum,
          size: PAGE_SIZE,
          mediaType: typeFilter,
          search: debouncedSearch || null,
        });
      } else if (selection.view === VIEW_FOLDER && selection.folderId) {
        result = await mediaLibraryService.listByFolder(selection.folderId, {
          page: pageNum,
          size: PAGE_SIZE,
          mediaType: typeFilter,
          search: debouncedSearch || null,
        });
      } else {
        result = await mediaService.getUploadLibrary({
          page: pageNum,
          size: PAGE_SIZE,
          mediaType: typeFilter,
          search: debouncedSearch || null,
          sortBy: 'createdAt',
          sortDir,
          dateFrom: dateFrom || null,
          dateTo: dateTo || null,
        });
      }

      const newItems = result.content || [];
      setItems(prev => reset ? newItems : [...prev, ...newItems]);
      setHasMore(!result.last && newItems.length === PAGE_SIZE);
    } catch (err) {
      console.error('[MediaLibraryPicker] Failed to fetch library:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [typeFilter, debouncedSearch, sortDir, dateFrom, dateTo, selection]);

  const handleDateRangeConfirm = ({ from, to }) => {
    setDateFrom(from);
    setDateTo(to);
    setShowDateRangePicker(false);
  };

  const handleDateRangeClear = () => {
    setDateFrom('');
    setDateTo('');
    setShowDateRangePicker(false);
  };

  const formatRangeChip = () => {
    if (!dateFrom || !dateTo) return 'Date range…';
    const f = new Date(dateFrom + 'T00:00:00');
    const t = new Date(dateTo   + 'T00:00:00');
    const sameYear = f.getFullYear() === t.getFullYear();
    const opts = { month: 'short', day: 'numeric', ...(!sameYear && { year: 'numeric' }) };
    return `${f.toLocaleDateString('en-US', opts)} – ${t.toLocaleDateString('en-US', opts)}`;
  };

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next);
  };

  const handleItemClick = (item, index) => {
    setPreviewIndex(index);
  };

  const handleToggleSelection = (e, item) => {
    e.stopPropagation();
    const id = item.id;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < maxSelections) {
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const selected = items.filter(item => selectedIds.has(item.id));
    onSelect(selected);
  };

  // Map MediaDTO / MediaLibraryItemDTO to the shape MediaGalleryGrid expects
  const gridItems = items.map(item => ({
    id: item.id,
    thumbnailUrl: item.thumbnailUrl || item.thumbnailVariants?.preview || item.qualityUrls?.original || item.displayUrl,
    url: item.qualityUrls?.original || item.displayUrl || item.thumbnailUrl || item.thumbnailVariants?.preview,
    mediaType: item.mediaType?.toLowerCase(),
    mimeType: item.mimeType,
    duration: item.duration,
    fileName: item.displayName || item.fileName,
    fileSize: item.fileSize,
    width: item.width,
    height: item.height,
    qualityUrls: item.qualityUrls || {},
    availableQualities: item.availableQualities || [],
    mediaFileId: item.id,
    isLibraryItem: true,
    selected: selectedIds.has(item.id),
  }));

  const getMediaForModal = (item) => {
    if (!item) return null;
    return {
      ...item,
      fileUrl: item.url,
      type: item.mediaType?.toLowerCase() || (item.mimeType?.startsWith('video') ? 'video' : 'image'),
      qualityUrls: item.qualityUrls || {},
      availableQualities: item.availableQualities || [],
    };
  };

  const footerActions = (
    <FooterRow>
      <SecondaryButton $rounded onClick={onClose}>Cancel</SecondaryButton>
      <PrimaryButton $rounded onClick={handleConfirm} disabled={selectedIds.size === 0}>
        Add
      </PrimaryButton>
    </FooterRow>
  );

  return (
    <>
    <Modal
      isOpen
      onClose={onClose}
      maxWidth="860px"
      title="Upload Library"
      subtitle={undefined}
      footer={footerActions}
      closeOnOverlayClick
    >
      <PickerContent>
        <PickerBody>
          <MainWrap>
            <TabRow $variant="pill">
              {tabItems.map((t) => (
                <TabItem
                  key={t.id}
                  $variant="pill"
                  $active={activeTabId === t.id}
                  onClick={() => handleTabClick(t.id)}
                >
                  {t.label}
                </TabItem>
              ))}
            </TabRow>
            <Toolbar>
                <ToolbarRow>
                  {TYPE_FILTERS.map(({ label, value }) => (
                    <FilterButton
                      key={label}
                      $active={typeFilter === value}
                      onClick={() => setTypeFilter(value)}
                    >
                      {label}
                    </FilterButton>
                  ))}
                </ToolbarRow>
                <ToolbarRow>
                  <SearchInput
                    type="text"
                    placeholder={isMobile ? 'Search…' : 'Search by filename…'}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Search media"
                  />
                  {selection.view === VIEW_ALL && (
                    <>
                      <SortButton
                        $active={sortDir === 'desc'}
                        onClick={() => setSortDir('desc')}
                        title="Newest first"
                      >
                        <VipIcon.ChevronDown size={14} />
                      </SortButton>
                      <SortButton
                        $active={sortDir === 'asc'}
                        onClick={() => setSortDir('asc')}
                        title="Oldest first"
                      >
                        <VipIcon.ChevronUp size={14} />
                      </SortButton>
                      {dateFrom && dateTo ? (
                        <DateRangeChip onClick={() => setShowDateRangePicker(true)}>
                          {formatRangeChip()}
                          <ChipClear
                            onClick={(e) => { e.stopPropagation(); handleDateRangeClear(); }}
                            title="Clear date range"
                          >
                            <VipIcon.CloseXCircle size={16} />
                          </ChipClear>
                        </DateRangeChip>
                      ) : (
                        <SortButton
                          onClick={() => setShowDateRangePicker(true)}
                          title="Filter by date range"
                        >
                          <VipIcon.Calendar size={14} />
                        </SortButton>
                      )}
                    </>
                  )}
                </ToolbarRow>
              </Toolbar>

            {loading ? (
              <LoadingContainer><LoadingSpinner /></LoadingContainer>
            ) : gridItems.length === 0 ? (
              <EmptyState>
                <p>No media found</p>
                <small>
                  {selection.view === VIEW_FAVORITES
                    ? 'Star media from the library to see it here'
                    : selection.view === VIEW_FOLDER
                      ? 'This folder is empty'
                      : (debouncedSearch || typeFilter
                          ? 'Try clearing the filters'
                          : 'Upload media to posts or messages and it will appear here once approved')}
                </small>
              </EmptyState>
            ) : (
              <GridContainer>
                <MediaGalleryGrid
                  items={gridItems}
                  onItemClick={handleItemClick}
                  columns={4}
                  columnsDesktop={6}
                  renderOverlay={(item, index) => (
                    <>
                      {item.selected && <SelectionOverlay />}
                      <SelectionCheckbox
                        $selected={item.selected}
                        onClick={(e) => handleToggleSelection(e, item)}
                        title={item.selected ? 'Deselect' : 'Select'}
                      >
                        {item.selected
                          ? <VipIcon.CheckmarkCircle size={20} />
                          : <VipIcon.CircleOutline size={20} />}
                      </SelectionCheckbox>
                    </>
                  )}
                />
                {hasMore && (
                  <LoadMoreContainer>
                    <LoadMoreButton onClick={handleLoadMore} disabled={loadingMore}>
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </LoadMoreButton>
                  </LoadMoreContainer>
                )}
              </GridContainer>
            )}
          </MainWrap>
        </PickerBody>
      </PickerContent>
    </Modal>

    {ReactDOM.createPortal(
      <MediaModal
        isOpen={previewIndex !== null}
        media={getMediaForModal(gridItems[previewIndex])}
        onClose={() => setPreviewIndex(null)}
        mediaList={gridItems.map(getMediaForModal)}
        currentIndex={previewIndex ?? 0}
        onNavigate={(index) => setPreviewIndex(index)}
      />,
      document.body
    )}

    {ReactDOM.createPortal(
      <DateRangePickerModal
        isOpen={showDateRangePicker}
        onClose={() => setShowDateRangePicker(false)}
        onConfirm={handleDateRangeConfirm}
        onClear={handleDateRangeClear}
        initialFrom={dateFrom}
        initialTo={dateTo}
      />,
      document.body
    )}
  </>
  );
};

export default MediaLibraryPicker;
